<script setup>
import { ref, computed, watch } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import axios from '@axios'
import { useDesignSystem } from '@/composables/useDesignSystem'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  member: {
    type: Object,
    default: null, // { _id, firstName, lastName, profileImageId, title, company }
  },
})

const emit = defineEmits(['update:modelValue', 'preferred-updated'])

const { dialogWidth } = useDesignSystem()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const loading = ref(false)
const loadError = ref(null)
const records = ref([])
const eligibleGroups = ref([])
const mutatingKey = ref(null) // 'restaurant:<id>' | 'group:<id>' | 'all-restaurants'

const restaurants = ref([])
const loadingScope = ref(false)
let scopeLoaded = false

const singleGroup = computed(() => (
  eligibleGroups.value.length === 1 ? eligibleGroups.value[0] : null
))

function findRestaurantRecord(restaurantId) {
  return records.value.find(
    r => r.scope === 'restaurant' && r.restaurantId?.toString() === restaurantId?.toString(),
  ) || null
}

function findGroupRecord(groupId) {
  return records.value.find(
    r => r.scope === 'group' && r.scopeUserGroupId?.toString() === groupId?.toString(),
  ) || null
}

function isPreferredViaGroup(restaurantId, groupId) {
  if (!groupId) return false

  return records.value.some(
    r => r.scope === 'group' && r.scopeUserGroupId?.toString() === groupId?.toString(),
  )
}

async function loadScope() {
  if (scopeLoaded) return
  loadingScope.value = true
  try {
    const { data } = await axios.get('/user/me/restaurant-scope')
    restaurants.value = Array.isArray(data?.restaurants) ? data.restaurants : []
    scopeLoaded = true
  } catch {
    restaurants.value = []
  } finally {
    loadingScope.value = false
  }
}

async function loadRecords(memberUserId) {
  loading.value = true
  loadError.value = null
  try {
    const { data } = await axios.get(`/preferred-member/member/${memberUserId}`)
    records.value = Array.isArray(data?.records) ? data.records : []
    eligibleGroups.value = Array.isArray(data?.eligibleGroups) ? data.eligibleGroups : []
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Failed to load Preferred status'
  } finally {
    loading.value = false
  }
}

function emitUpdate() {
  const restaurantIds = records.value
    .filter(r => r.scope === 'restaurant' && r.restaurantId)
    .map(r => r.restaurantId.toString())
  const groupIds = records.value
    .filter(r => r.scope === 'group' && r.scopeUserGroupId)
    .map(r => r.scopeUserGroupId.toString())
  emit('preferred-updated', {
    memberUserId: props.member?._id,
    isPreferred: records.value.length > 0,
    restaurantIds,
    groupIds,
  })
}

async function markAtRestaurant(restaurantId) {
  const memberUserId = props.member?._id
  if (!memberUserId) return
  mutatingKey.value = `restaurant:${restaurantId}`
  loadError.value = null
  try {
    await axios.post('/preferred-member/restaurant', { memberUserId, restaurantId })
    await loadRecords(memberUserId)
    emitUpdate()
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Failed to mark as Preferred'
  } finally {
    mutatingKey.value = null
  }
}

async function markForGroup(userGroupId) {
  const memberUserId = props.member?._id
  if (!memberUserId) return
  mutatingKey.value = `group:${userGroupId}`
  loadError.value = null
  try {
    await axios.post('/preferred-member/group', { memberUserId, userGroupId })
    await loadRecords(memberUserId)
    emitUpdate()
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Failed to mark as Preferred for group'
  } finally {
    mutatingKey.value = null
  }
}

async function removeRecord(record) {
  const memberUserId = props.member?._id
  if (!memberUserId || !record?.preferredId) return
  mutatingKey.value = record.scope === 'group'
    ? `group:${record.scopeUserGroupId}`
    : `restaurant:${record.restaurantId}`
  loadError.value = null
  try {
    await axios.delete(`/preferred-member/${record.preferredId}`)
    await loadRecords(memberUserId)
    emitUpdate()
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Failed to remove Preferred status'
  } finally {
    mutatingKey.value = null
  }
}

const allRestaurantsPreferred = computed(() => {
  if (restaurants.value.length === 0) return false

  return restaurants.value.every(r => Boolean(findRestaurantRecord(r._id))
    || isPreferredViaGroup(r._id, singleGroup.value?._id))
})

const restaurantsToMark = computed(() => restaurants.value.filter(r => !findRestaurantRecord(r._id)
  && !isPreferredViaGroup(r._id, singleGroup.value?._id)))

async function markAtAllRestaurants() {
  const memberUserId = props.member?._id
  if (!memberUserId) return
  const targets = restaurantsToMark.value
  if (targets.length === 0) return

  mutatingKey.value = 'all-restaurants'
  loadError.value = null
  try {
    const results = await Promise.allSettled(
      targets.map(r => axios.post('/preferred-member/restaurant', {
        memberUserId,
        restaurantId: r._id,
      })),
    )
    await loadRecords(memberUserId)
    emitUpdate()
    const failures = results.filter(r => r.status === 'rejected').length
    if (failures > 0) {
      loadError.value = `Could not mark Preferred at ${failures} of ${targets.length} restaurant${targets.length !== 1 ? 's' : ''}. Please retry.`
    }
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Failed to mark Preferred at all restaurants'
    try { await loadRecords(memberUserId) } catch { /* ignore */ }
    emitUpdate()
  } finally {
    mutatingKey.value = null
  }
}

// Load data when the dialog opens for a member. Scope is cached across opens.
watch(
  () => [props.modelValue, props.member?._id],
  async ([open, memberId]) => {
    if (!open || !memberId) return
    records.value = []
    eligibleGroups.value = []
    loadError.value = null
    await loadScope()
    await loadRecords(memberId)
  },
  { immediate: true },
)
</script>

<template>
  <VDialog
    v-model="isOpen"
    :width="dialogWidth('sm')"
    :max-width="500"
  >
    <VCard>
      <VCardTitle class="pmd__title">
        <div
          v-if="member"
          class="pmd__member"
        >
          <div class="pmd__avatar">
            <VImg
              v-if="member.profileImageId"
              :src="mediaUrl(member.profileImageId, 'small')"
              class="pmd__avatar-img"
              cover
            />
            <span
              v-else
              class="pmd__avatar-initials"
            >
              {{ (member.firstName || '').charAt(0).toUpperCase() }}{{ (member.lastName || '').charAt(0).toUpperCase() }}
            </span>
          </div>
          <div class="pmd__member-text">
            <div class="pmd__member-name">
              {{ member.firstName }} {{ member.lastName }}
            </div>
            <div
              v-if="member.title || member.company"
              class="pmd__member-role"
            >
              <template v-if="member.title && member.company">
                {{ member.title }}, {{ member.company }}
              </template>
              <template v-else>
                {{ member.title || member.company }}
              </template>
            </div>
          </div>
        </div>
      </VCardTitle>
      <VCardText class="pmd__body">
        <VAlert
          v-if="loadError"
          type="error"
          variant="tonal"
          density="compact"
          closable
          class="mb-3"
          @click:close="loadError = null"
        >
          {{ loadError }}
        </VAlert>

        <div
          v-if="loading || loadingScope"
          class="pmd__loading"
        >
          <VProgressCircular
            indeterminate
            size="24"
            color="primary"
          />
        </div>

        <template v-else>
          <div
            v-if="restaurants.length === 0"
            class="pmd__empty"
          >
            No restaurants in your scope.
          </div>

          <!-- Bulk action: mark at all restaurants -->
          <div
            v-if="restaurants.length > 1"
            class="pmd__bulk"
          >
            <VBtn
              v-if="!allRestaurantsPreferred"
              color="primary-dark"
              variant="flat"
              prepend-icon="mdi-star-plus-outline"
              block
              :loading="mutatingKey === 'all-restaurants'"
              :disabled="!!mutatingKey && mutatingKey !== 'all-restaurants'"
              class="pmd__bulk-btn"
              @click="markAtAllRestaurants"
            >
              Mark Preferred at all {{ restaurants.length }} restaurants
            </VBtn>
            <div
              v-else
              class="pmd__bulk-done"
            >
              <VIcon
                color="success"
                size="18"
              >
                mdi-check-circle
              </VIcon>
              <span>Preferred at all your restaurants</span>
            </div>
            <div class="pmd__hint">
              <p>Marking as Preferred allows {{ member?.firstName || 'this member' }} to submit reservation requests without a guaranteed spend.</p>
              <p>{{ member?.firstName || 'They' }} will see this status when booking.</p>
            </div>
          </div>

          <!-- Per-restaurant rows -->
          <div
            v-for="r in restaurants"
            :key="r._id"
            class="pmd__row"
          >
            <div class="pmd__info">
              <div class="pmd__rest-name">
                {{ r.name }}
              </div>
              <div
                v-if="isPreferredViaGroup(r._id, singleGroup?._id)"
                class="pmd__via"
              >
                Via {{ singleGroup.name }}
              </div>
            </div>
            <div class="pmd__actions-row">
              <template v-if="findRestaurantRecord(r._id)">
                <VBtn
                  size="small"
                  color="error"
                  variant="outlined"
                  :loading="mutatingKey === `restaurant:${r._id}`"
                  :disabled="!!mutatingKey && mutatingKey !== `restaurant:${r._id}`"
                  @click="removeRecord(findRestaurantRecord(r._id))"
                >
                  Remove
                </VBtn>
              </template>
              <template v-else>
                <VBtn
                  size="small"
                  color="primary-dark"
                  variant="flat"
                  :loading="mutatingKey === `restaurant:${r._id}`"
                  :disabled="
                    (!!mutatingKey && mutatingKey !== `restaurant:${r._id}`)
                      || isPreferredViaGroup(r._id, singleGroup?._id)
                  "
                  @click="markAtRestaurant(r._id)"
                >
                  Mark Preferred
                </VBtn>
              </template>
            </div>
          </div>

          <!-- Group-scope row (only when caller has exactly one eligible group) -->
          <div
            v-if="singleGroup"
            class="pmd__row pmd__row--group"
          >
            <div class="pmd__info">
              <div class="pmd__rest-name">
                Preferred across {{ singleGroup.name }}
              </div>
              <div class="pmd__via">
                Applies to all restaurants in this group
              </div>
            </div>
            <div class="pmd__actions-row">
              <template v-if="findGroupRecord(singleGroup._id)">
                <VBtn
                  size="small"
                  color="error"
                  variant="outlined"
                  :loading="mutatingKey === `group:${singleGroup._id}`"
                  :disabled="!!mutatingKey && mutatingKey !== `group:${singleGroup._id}`"
                  @click="removeRecord(findGroupRecord(singleGroup._id))"
                >
                  Remove
                </VBtn>
              </template>
              <template v-else>
                <VBtn
                  size="small"
                  color="primary-dark"
                  variant="flat"
                  :loading="mutatingKey === `group:${singleGroup._id}`"
                  :disabled="!!mutatingKey && mutatingKey !== `group:${singleGroup._id}`"
                  @click="markForGroup(singleGroup._id)"
                >
                  Mark Preferred
                </VBtn>
              </template>
            </div>
          </div>
        </template>
      </VCardText>
      <VCardActions class="pb-4 px-4">
        <VSpacer />
        <VBtn
          variant="text"
          @click="isOpen = false"
        >
          Done
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.pmd__title {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 20px 20px 12px;
  line-height: 1.3;
}

.pmd__heading {
  font-size: 18px;
  font-weight: 600;
}

.pmd__member {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.pmd__avatar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface), 0.72);
}

.pmd__avatar-img {
  width: 100%;
  height: 100%;
}

.pmd__avatar-initials {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.pmd__member-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pmd__member-name {
  font-size: 16px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  white-space: normal;
  overflow-wrap: anywhere;
}

.pmd__member-role {
  font-size: 13px;
  font-weight: 400;
  color: rgb(var(--v-theme-on-surface), 0.72);
  white-space: normal;
  overflow-wrap: anywhere;
}

.pmd__body {
  padding: 4px 20px 12px;
}

.pmd__loading {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.pmd__empty {
  padding: 16px 0;
  color: rgb(var(--v-theme-on-surface), 0.72);
  font-size: 14px;
}

.pmd__bulk {
  margin-bottom: 16px;
}

.pmd__bulk-btn {
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.pmd__bulk-done {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-success), 0.08);
  color: rgb(var(--v-theme-on-surface));
  font-size: 14px;
}

.pmd__hint {
  margin-top: 10px;

  p {
    margin: 0;
    color: rgb(var(--v-theme-on-surface), 0.56);
    font-size: 12.5px;
    line-height: 1.5;
  }

  p + p {
    margin-top: 4px;
  }
}

.pmd__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgb(var(--v-theme-on-surface), 0.08);
}

.pmd__row:last-child {
  border-bottom: none;
}

.pmd__row--group {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--v-theme-on-surface), 0.08);
}

.pmd__info {
  flex: 1 1 auto;
  min-width: 0;
}

.pmd__rest-name {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.pmd__via {
  margin-top: 2px;
  font-size: 12px;
  color: rgb(var(--v-theme-on-surface), 0.64);
}

.pmd__actions-row {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}
</style>
